/**
 * useOrgAccess Hook
 * Queries v_org_effective_access view to determine which apps the user can access
 * Used for conditional rendering of "Switch to FMA" and "Switch to AMS" buttons
 */
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/global/auth/AuthContext';

interface OrgAccessData {
  organization_id: string;
  tenantid: string;
  organization_name: string;
  organization_type: string;
  has_fma_access: boolean;
  fma_plan: string | null;
  has_ams_access: boolean;
  ams_entitlement_details: any;
  has_active_billing: boolean;
  subscription_status: string | null;
  trial_ends_at: string | null;
  user_is_member: boolean;
}

interface UseOrgAccessResult {
  canAccessFMA: boolean;
  canAccessAMS: boolean;
  fmaPlan: string | null;
  subscriptionStatus: string | null;
  loading: boolean;
  error: Error | null;
}

/**
 * Hook to check organization access permissions for FMA and AMS apps
 * 
 * @returns {UseOrgAccessResult} Access permissions and loading state
 * 
 * @example
 * const { canAccessFMA, canAccessAMS, loading } = useOrgAccess();
 * 
 * if (loading) return <Spinner />;
 * 
 * return (
 *   <>
 *     {canAccessFMA && <Button onClick={goToFMA}>Switch to FMA</Button>}
 *     {canAccessAMS && <Button onClick={goToAMS}>Switch to AMS</Button>}
 *   </>
 * );
 */
export function useOrgAccess(): UseOrgAccessResult {
  const { activeTenantId } = useAuth();

  const { data, isLoading, error } = useQuery<OrgAccessData | null, Error>({
    queryKey: ['org-access', activeTenantId],
    queryFn: async () => {
      if (!activeTenantId) return null;

      const { data, error } = await supabase
        .from('v_org_effective_access')
        .select('*')
        .eq('organization_id', activeTenantId)
        .single();

      if (error) throw error;
      return data as OrgAccessData;
    },
    enabled: !!activeTenantId,
    staleTime: 5 * 60 * 1000, // Cache for 5 minutes
    retry: 2,
  });

  return {
    canAccessFMA: data?.has_fma_access ?? false,
    canAccessAMS: (data?.has_ams_access && data?.has_active_billing) ?? false,
    fmaPlan: data?.fma_plan ?? null,
    subscriptionStatus: data?.subscription_status ?? null,
    loading: isLoading,
    error: error,
  };
}
